import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProfile } from '../hooks/useProfile';

const Navbar: React.FC = () => {
    const { user, signOut } = useAuth();
    const { profile } = useProfile();
    const location = useLocation();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        if (location.pathname !== '/') {
            navigate('/#' + id);
            return;
        }
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setMenuOpen(false);
    };

    // Handle hash scrolling on navigation
    useEffect(() => {
        if (location.hash && location.pathname === '/') {
            const id = location.hash.substring(1);
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    }, [location]);

    const landingLinks = [
        { id: 'features', label: 'Features' },
        { id: 'how-it-works', label: 'How it Works' },
        { id: 'pricing', label: 'Pricing' },
    ];

    const navLinks = [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/generator', label: 'Generator' },
        { to: '/upgrade', label: 'Upgrade' },
    ];

    const planBadgeClass: Record<string, string> = {
        free: 'badge-free',
        creator: 'badge-creator',
        pro: 'badge-pro',
        lifetime: 'bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-lg shadow-amber-500/20',
    };

    return (
        <nav className={`fixed top-0 z-[100] w-full transition-all duration-300 border-b ${scrolled ? 'bg-[#0F172A]/80 backdrop-blur-md border-[#334155] py-0' : 'bg-transparent border-transparent py-2'}`}>
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between h-20">

                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group">
                        <img
                            src="/logo.png"
                            alt="Share Visuals"
                            className="h-11 w-auto"
                        />
                        <span className="text-xl font-bold text-white group-hover:text-primary-400 transition-colors">Share Visuals</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {landingLinks.map((link) => (
                            <button
                                key={link.id}
                                onClick={() => scrollToSection(link.id)}
                                className="text-sm font-bold text-dark-400 hover:text-white transition-colors"
                            >
                                {link.label}
                            </button>
                        ))}
                        {user && (
                            <div className="h-4 w-px bg-white/10 mx-2" />
                        )}
                        {user && navLinks.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={`text-sm font-bold transition-colors ${location.pathname === link.to ? 'text-primary-400' : 'text-dark-400 hover:text-white'}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center gap-4">
                        {user ? (
                            <div className="flex items-center gap-4">
                                {profile && (
                                    <span className={`hidden sm:inline-flex px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${planBadgeClass[profile.plan]}`}>
                                        {profile.plan}
                                    </span>
                                )}
                                <button
                                    onClick={signOut}
                                    className="hidden sm:block text-sm font-bold text-dark-400 hover:text-white transition-colors"
                                >
                                    Sign Out
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-4">
                                <Link to="/login" className="hidden sm:block text-sm font-bold text-dark-400 hover:text-white transition-colors">
                                    Sign In
                                </Link>
                                <Link
                                    to="/signup"
                                    className="hidden md:block px-6 py-3 rounded-xl bg-white text-dark-950 text-sm font-black hoverScale transition-all shadow-glow-white active:scale-95"
                                >
                                    Get Started
                                </Link>
                            </div>
                        )}

                        {/* Mobile Menu Button */}
                        <button
                            className="md:hidden p-2 text-dark-400 hover:text-white transition-colors"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label={menuOpen ? "Close menu" : "Open menu"}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {menuOpen
                                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                                }
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {menuOpen && (
                    <div className="md:hidden border-t border-white/5 py-6 space-y-4 animate-slide-down bg-[#0F172A]">
                        {landingLinks.map((link) => (
                            <button
                                key={link.id}
                                onClick={() => scrollToSection(link.id)}
                                className="block w-full text-left px-4 text-base font-bold text-dark-300 hover:text-white"
                            >
                                {link.label}
                            </button>
                        ))}
                        {user && navLinks.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                onClick={() => setMenuOpen(false)}
                                className="block px-4 text-base font-bold text-dark-300 hover:text-white"
                            >
                                {link.label}
                            </Link>
                        ))}
                        {!user && (
                            <div className="px-4 space-y-4">
                                <Link
                                    to="/login"
                                    onClick={() => setMenuOpen(false)}
                                    className="block text-base font-bold text-dark-300 hover:text-white"
                                >
                                    Sign In
                                </Link>
                                <Link
                                    to="/signup"
                                    onClick={() => setMenuOpen(false)}
                                    className="block w-full py-4 text-center rounded-2xl bg-white text-dark-950 font-black text-sm"
                                >
                                    Get Started
                                </Link>
                            </div>
                        )}
                        {user && (
                            <button
                                onClick={() => {
                                    signOut();
                                    setMenuOpen(false);
                                }}
                                className="block w-full text-left px-4 text-base font-bold text-danger-400"
                            >
                                Sign Out
                            </button>
                        )}
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
